
<template>
  <div>
    <div>
      <div v-if="dialogue.title && !resolvedComponent" class="section-titre d-flex align-center py-5">
        {{ dialogue.title }}
      </div>
      <div id="HTML" class="" style="overflow: auto; max-height: 75vh;" @scroll="onScroll">
        <!-- Composant riche disponible dans le registre -->
        <component v-if="resolvedComponent"
                   :is="resolvedComponent"
                   v-bind="paramsRecord"
                   :dialog-title="dialogue.title"
                   :cancel-label="dialogue.buttons[2] || 'Annuler'"
                   @set-button-label="setButtonLabel"
                   @set-button-disabled="setButtonDisabled"
                   @set-button-empty="hideButton"
                   @cancel="action(2)"
                   @close-dialog="$emit('closeDialog')" />
        <!-- Dialogue standard généré côté serveur -->
        <div v-else @click="copyText" v-html="dialogue.label"></div>
      </div>

      <div v-if="hasEdit">
        <edit-phone-number v-if="getRule('type') == 'phone'"
                           ref="EDIT_DIALOG"
                           class="pl-2"
                           :bold="bold"
                           :hideLabel="true"
                           :context-id=0
                           :data-table=false
                           :disabled=false
                           :edit-field-info=editFieldInfo
                           :required=true
                           :min="editMin"
                           :max="editMax"
                           :value=saisie
                           @error=setError
                           @updated=setUpdated>
        </edit-phone-number>

        <edit-field v-else
                    ref="EDIT_DIALOG"
                    :bold="bold"
                    :context-id=0
                    :data-table=false
                    :disabled=false
                    :edit-field-info=editFieldInfo
                    :required=true
                    :min="editMin"
                    :max="editMax"
                    :value=saisie
                    @error=setError
                    @updated=setUpdated>
        </edit-field>
      </div>
      <div v-for="(check, index) in dialogue.checks" :key="index">
        <v-row>
          <div>
            <check-field ref="CHECK"
                         :data-table="true"
                         :context-id=0
                         :value=checked[index]
                         :edit-field-info=checkFieldInfo(index)
                         @updated=setChecked(index)>
            </check-field>
          </div>
          <v-row class="align-content-center ma-0">
            <div v-if="checkLabelHtml(index)" @click.stop="setChecked(index)"
                 v-html="checkLabelHtml(index)"></div>
          </v-row>
        </v-row>
      </div>
      <div v-if="hasSelect" class="px-10">
        <select-reason :ReasonSelect=dialogue.reason
                       @updated="setSelect">
        </select-reason>
      </div>
      <div v-if="hasRows">
        <v-table class="fraction-dsn-table" dense>
          <tbody>
            <tr v-for="(row, indexRow) in dialogue.wizardRows" :key="indexRow">

              <td v-for="(col, indexCol) in row.wizardCols" :key="indexCol" class="edit-field text-left">{{ col }}</td>
              <td>
                <div class="d-flex">
                  <div class="d-flex justify-center" @click.stop="editItem(indexRow)">
                    <v-icon color="#2a97da" size="23">fas fa-pencil</v-icon>
                  </div>
                  <div class="d-flex justify-center" @click.stop="deleteItem(indexRow)">
                    <v-icon color="#e42125" size="23">fas fa-trash-can</v-icon>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>
      <div class="d-flex flex-row-reverse pt-2">
        <div v-for="(button, index) in dialogue.buttons" :key="index">
          <loader-button v-if="button != '' && !(resolvedComponent && index === 2) && !componentHidden[index]"
                         class="ms-2"
                         :label=buttonLabel(index)
                         :outlined="index > 0"
                         :loading="loading[index]"
                         :disabled="disabled[index] || !!componentDisabled[index]"
                         @callback="action(index)">
          </loader-button>
        </div>
        <div v-if="dialogue.wizardSteps.length > 1">
          <loader-button class="me-10"
                         label='Précédent'
                         :outlined="true"
                         :loading="previousLoading"
                         @callback="previous()">
          </loader-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Dialog, { DialogAnswers, DialogAnswerData, DialogCheck } from "@/generated/classes/Dialog";
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import EditBase from '@/framework/base/components/EditBase';
  import CheckField from "@/framework/base/components/CheckField.vue";
  import EditField from '@/framework/base/components/EditField.vue';
  import Item from '@/framework/base/classes/Item';
  import SelectReason from '@/framework/dialog/components/SelectReason.vue';
  import SelectFieldVue from "@/framework/base/components/SelectField.vue";
  import { EditFieldType, FieldInfo, EditFieldTheme } from '@/framework/base/components/FieldInfo';
  import { MapRessource } from '@/framework/base/helpers/Ressources';
  import Ressource from '@/framework/ressource/classes/Ressource';
  import { FieldError } from "@/framework/base/components/FieldInfo";
  import validator, { rules, RULES_FRENCH_LABEL } from '@/generated/validators/ValidationRules';
  import DialogHubManager from '@/framework/dialog/helpers/DialogHubManager';
  import { defineComponent, type PropType, type Component } from "vue";
  import { hasHtml } from "@/framework/base/helpers/String.ts";
  import { getDialogComponent } from '@/framework/dialog/helpers/DialogLoader';
  import { ReasonSelect } from '@/extend/classes/Reason';
  import EditPhoneNumber from '@/framework/base/components/EditPhoneNumber.vue'

  export default defineComponent({
    components: {
      EditField,
      CheckField,
      SelectReason,
      LoaderButton,
      EditPhoneNumber
    },
    extends: EditBase,
    props: {
      dialogue: {
        type: Object as PropType<Dialog>,
        required: true
      }
    },
    data () {
      const loading: boolean[] = [false, false, false];
      const disabled: boolean[] = [];
      const checked: boolean[] = [];
      const refDialogue = this.dialogue;
      const buttonLabels: Record<number, string> = {};

      return {
        error: false,
        saisie: '',
        checked,
        disabled,
        loading,
        previousLoading: false,
        scrollable: false,
        choix: '',
        refDialogue,
        copied: false,
        buttonLabels,
        confirmOverride:   null as (() => void) | null,
        componentDisabled: {} as Record<number, boolean>,
        componentHidden: {} as Record<number, boolean>,
        unregisterRefresh: null as (() => void) | null,
      };
    },
    computed: {
      choixFieldInfo (): FieldInfo {
        const ressource = new MapRessource();
        const type = EditFieldType.SELECT;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
      },
      editFieldInfo (): FieldInfo {
        const type = this.type;
        const precision = 0;
        const format = null;
        const mask = this.mask;
        const focus = this.focus;
        const live = this.live;
        return new FieldInfo(new MapRessource(), type, precision, mask, format, focus, live);
      },
      resolvedComponent (): Component | null {
        if (!this.dialogue.dialogName) return null;
        return getDialogComponent(this.dialogue.dialogName);
      },
      paramsRecord (): Record<string, string> {
        return Object.fromEntries(
          (this.dialogue.publiParams ?? []).map(p => [p.key ?? '', p.value ?? ''])
        );
      },
      hasEdit (): boolean {
        return this.dialogue.rules != undefined && this.dialogue.rules != null && this.refDialogue.rules != '';
      },
      hasSelect (): boolean {
        return this.dialogue.reason && this.refDialogue.reason.choix.length > 0;
      },
      hasRows (): boolean {
        return this.dialogue.wizardRows && this.refDialogue.wizardRows.length > 0;
      },
      type(): EditFieldType {
        const type = this.getRule('type') as string;
        if (type == 'int') {
          return EditFieldType.INT;
        } else if (type == 'phone') {
          return EditFieldType.PHONE;
        } else {
          return EditFieldType.STRING;
        }
      },
      editMin (): number {
        return this.getRule('min') as number;
      },
      editMax (): number {
        return this.getRule('max') as number;
      }
    },
    provide () {
      return {
        setDialogWidth: (width: number) => {
          this.$emit('setSize', false, false, width);
        },
        registerOnConfirm: (fn: () => void) => {
          this.confirmOverride = fn;
        },
      };
    },
    created () {
      this.setDialog();
    },
    mounted () {
      this.unregisterRefresh = DialogHubManager.waitRefresh(this.updateDialog);
      document.addEventListener("keydown", this.onKeydown)
      this.hasScroll();
    },
    beforeUnmount () {
      document.removeEventListener('keydown', this.onKeydown)
      this.unregisterRefresh?.();
    },

    methods: {
      onScroll (event: any) {
        if (Math.ceil(event.target.scrollTop) + event.target.clientHeight >= event.target.scrollHeight) {
          this.disabled = [false, false, false];
        }
      },
      hasScroll (): void {
        this.$nextTick(() => {
          const div = document.getElementById('HTML');
          if (div && !this.resolvedComponent) {
            this.scrollable = div.scrollHeight > div.clientHeight;
            if (this.scrollable) {
              this.disabled = [true, false, false];
            }
          }
        })
      },
      onKeydown (event: KeyboardEvent) {
        if (event.key == 'Enter' || event.key == 'Escape') {
          event.preventDefault();
          this.action(event);
        }
      },
      setDialog () {
        this.checked = [];
        this.buttonLabels = {};
        let hasCheck = false;
        if (this.refDialogue.checks) {
          this.refDialogue.checks.forEach((check: DialogCheck) => {
            this.checked.push(check.value);
            if (check.required && !check.value) {
              hasCheck = true;
            }
          });
        }
        let hasSelect = false;
        if (this.refDialogue.reason) {
          if (this.refDialogue.reason.value == 0) {
            hasSelect = true;
          } else {
            const choix = this.refDialogue.reason.choix[this.refDialogue.reason.value - 1];
            if (choix.hasTextArea && choix.textRequired && this.refDialogue.reason.text == '') {
              hasSelect = true;
            }
          }
        }
        this.disabled = [this.hasEdit || hasCheck || hasSelect, false, false];
      },

      checkFieldInfo (index: number): FieldInfo {
        const ressource = new Ressource();
        if (!hasHtml(this.dialogue.checks[index].label)) {
          ressource.label = this.dialogue.checks[index].label;
        }
        const type = this.type;
        const precision = 0;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(ressource, type, precision, mask, format, focus, live);
      },
      checkLabelHtml (index: number): string {
        let res = '';
        if (hasHtml(this.dialogue.checks[index].label)) {
          res = this.dialogue.checks[index].label;
        }
        return res;
      },
      copyText (event: any) {
        if (!this.copied) {
          const spanElement = event.target.closest('.copy-text'); // Find the span
          if (spanElement) {
            const textToCopy = spanElement.textContent.trim();
            navigator.clipboard.writeText(textToCopy)
              .then(() => {
                const tooltip = document.createElement('span');
                tooltip.className = 'tooltip';
                tooltip.textContent = 'Texte copié'
                spanElement.appendChild(tooltip);
                this.copied = true;
                setTimeout(() => this.endCopyText(tooltip), 1000);
              })
              .catch((err) => {
                console.error('Failed to copy:', err);
              });
          }
        }
      },
      endCopyText (tooltip: HTMLSpanElement) {
        tooltip.remove();
        this.copied = false;
      },
      getRule (value: string): string | number {
        let res = '';
        if (this.refDialogue.rules) {
          this.refDialogue.rules.split(',').forEach((rule) => {
            if (rule.trim().startsWith(value)) {
              res = rule.split(':')[1].trim();
            }
          })
        }
        return res;
      },
      mask (value: string): string {
        return this.getRule('mask') as string;
      },
      focus (value: string): validator | null {
        let res: validator | null = null;
        const focus = this.getRule('focus');
        if (focus != '') {
          // @ts-expect-error : generated method
          const rule = rules[focus];
          if (rule) {
            // @ts-expect-error : generated method
            const labels = RULES_FRENCH_LABEL.text[focus].error;
            res = new validator(rule, labels);
          } else {
            this.refDialogue.label = '<div>FOCUS INCONNU : ' + focus + '</div><br><br>' + this.refDialogue.label;
          }
        }
        return res;
      },
      live (value: string): validator | null {
        let res: validator | null = null;
        const live = this.getRule('live');
        if (live != '') {
          // @ts-expect-error : generated method
          const rule = rules[live];
          if (rule) {
            // @ts-expect-error : generated method
            const labels = RULES_FRENCH_LABEL.text[live].error;
            res = new validator(rule, labels);
          } else {
            this.refDialogue.label = '<div>FOCUS INCONNU : ' + live + '</div><br><br>' + this.refDialogue.label;
          }
        }
        return res;
      },
      setUpdated (value: string) {
        this.saisie = value;
        let notChecked = this.error;
        this.checked.forEach((check, index) => {
          if (!check && this.refDialogue.checks[index].required) {
            notChecked = true;
          }
        });
        this.disabled = [notChecked, false, false];
      },
      setError (value: FieldError) {
        this.error = value.error;
        this.disabled = [this.error, false, false];
      },
      setChecked (index: number) {
        this.checked[index] = !this.checked[index];
        this.refDialogue.checks[index].value = this.checked[index];
        let okDisabled = false;
        this.checked.forEach((check, index2) => {
          if (!check && this.refDialogue.checks[index2].required) {
            okDisabled = true;
          }
        });
        this.disabled = [okDisabled, false, false];
      },
      setSelect (reason: ReasonSelect) {
        this.dialogue.reason = reason;
        this.disabled = [!reason.valid, false, false];
      },
      updateDialog (dialog: Dialog) {
        this.refDialogue.title = dialog.title;
        this.refDialogue.label = dialog.label;
        this.refDialogue.description = dialog.description;
        this.refDialogue.rules = dialog.rules;
        this.refDialogue.checks = dialog.checks;
        this.refDialogue.reason = dialog.reason;
        this.refDialogue.buttons = dialog.buttons;
        this.refDialogue.wizardSteps = dialog.wizardSteps;
        this.refDialogue.wizardData = dialog.wizardData;
        this.refDialogue.wizardRows = dialog.wizardRows;
        this.refDialogue.dialogName = dialog.dialogName;
        this.refDialogue.publiParams = dialog.publiParams;
      },
      previous () {
        this.previousLoading = true;
        this.refDialogue.answer = DialogAnswers.PREVIOUS;
        this.answer()
      },
      deleteItem (item: number) {
        this.refDialogue.data = new DialogAnswerData();
        this.refDialogue.answer = DialogAnswers.DELETE_ITEM;
        this.answer()
      },
      editItem (item: number) {
        this.refDialogue.data = new DialogAnswerData();
        this.refDialogue.answer = DialogAnswers.EDIT_ITEM;
        this.answer()
      },
      setButtonLabel (index: number, label: string) {
        this.buttonLabels[index] = label;
      },
      setButtonDisabled (index: number, disabled: boolean) {
        this.componentDisabled[index] = disabled;
      },
      hideButton (index: number) {
        this.componentHidden[index] = true;
      },
      buttonLabel (index: number): string {
        return this.buttonLabels[index] ?? this.dialogue.buttons[index];
      },
      action (event: KeyboardEvent | number) {
        let error = false;
        if (this.hasEdit) {
          // @ts-expect-error : touch
          this.$refs.EDIT_DIALOG.touch();
        }
        if (typeof event !== 'number' && event.key) {
          if (event.key == 'Enter') {
            if (this.disabled[0]) {
              error = true;
            } else {
              error = this.error;
              this.refDialogue.answer = DialogAnswers.CONFIRM;
              this.loading = [true, false, false];
            }
          } else if (event.key == 'Escape' && !this.disabled[2]) {
            if (this.scrollable) {
              error = true;
            }
            this.refDialogue.answer = DialogAnswers.CANCEL;
            this.loading = [false, false, true];
          }
        } else {
          const index = event as number;
          if (index == 0) {
            if (this.confirmOverride) {
              this.loading = [true, false, false];
              Promise.resolve(this.confirmOverride()).then(() => {
                this.loading = [false, false, false];
              }).catch(() => {
                this.loading = [false, false, false];
              });
              return;
            }
            this.loading = [true, false, false];
            error = this.error;
            this.refDialogue.answer = DialogAnswers.CONFIRM;
          } else if (index == 1) {
            this.loading = [false, true, false];
            this.refDialogue.answer = DialogAnswers.DENY;
          } else if (index == 2) {
            this.loading = [false, false, true];
            this.refDialogue.answer = DialogAnswers.CANCEL;
          }
        }
        if (!error) {
          this.refDialogue.data = new DialogAnswerData();
          this.refDialogue.data.value = this.hasSelect ? this.choix : this.saisie;
          this.refDialogue.data.checkValues = this.checked;
          this.answer();
        } else {
          this.loading = [false, false, false];
          this.previousLoading = false;
        }
      },
      answer () {
        Dialog.answer(this.refDialogue).then((res: any) => {
          this.loading = [false, false, false];
          this.previousLoading = false;
          if (res instanceof (Dialog)) {
            if (res.keepOpenned) {
              this.updateDialog(res);
              this.setDialog();
            } else {
              this.$emit('closeDialog');
            }
          } else if (!this.refDialogue.keepOpenned) {
            this.$emit('closeDialog')
          }
        }).catch(() => {
          this.loading = [false, false, false];
          this.previousLoading = false;
          if (!this.refDialogue.keepOpenned) {
            this.$emit('closeDialog')
          }
        });
      }
    }
  })

</script>

<style scoped>
  .copy-confirmation {
    color: green;
    font-weight: bold;
  }

</style>

